import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Boxes, Download, Upload, RefreshCw, FileArchive, Search,
  CheckCircle, XCircle, Trash2, Loader2, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const POLL_INTERVAL_MS = 1500;
const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB

const fmtMB = (mb) => `${(mb || 0).toFixed(2)} MB`;
const fmtNum = (n) => (n ?? 0).toLocaleString();

/**
 * Coerce any backend / network error into a plain string for `toast.error()`.
 * FastAPI returns pydantic validation errors as `detail: [{type, loc, msg, ...}]`;
 * passing that object directly to a toast crashes React with
 * "Objects are not valid as a React child".
 */
const errMsg = (err, fallback = 'Something went wrong') => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d)))
      .join('; ');
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || JSON.stringify(detail);
  }
  return err?.message || fallback;
};

export default function SelectiveBackupSection({ disabled = false, onChanged }) {
  const [collections, setCollections] = useState([]);
  const [totals, setTotals] = useState({ docs: 0, mb: 0 });
  const [loadingList, setLoadingList] = useState(false);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(new Set());

  const [exports, setExports] = useState([]);

  // Active selective job (export OR import)
  const [activeJob, setActiveJob] = useState(null);
  const pollRef = useRef(null);

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importUploadId, setImportUploadId] = useState(null);
  const [importInspect, setImportInspect] = useState(null);
  const [importSelected, setImportSelected] = useState(new Set());
  const [importStage, setImportStage] = useState('select'); // select | uploading | inspect | confirm
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importConfirmText, setImportConfirmText] = useState('');

  // ---------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------
  const loadAll = async () => {
    setLoadingList(true);
    try {
      const [listRes, expRes] = await Promise.all([
        apiClient.get('/backup/collections/list'),
        apiClient.get('/backup/collections/exports'),
      ]);
      setCollections(listRes.data.collections || []);
      setTotals({
        docs: listRes.data.total_documents || 0,
        mb: listRes.data.total_size_mb || 0,
      });
      setExports(expRes.data.exports || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load collections');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadAll();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return collections;
    return collections.filter((c) => c.name.toLowerCase().includes(q));
  }, [collections, filter]);

  const allSelectedInView = filtered.length > 0
    && filtered.every((c) => selected.has(c.name));

  const toggleAllInView = () => {
    const next = new Set(selected);
    if (allSelectedInView) {
      filtered.forEach((c) => next.delete(c.name));
    } else {
      filtered.forEach((c) => next.add(c.name));
    }
    setSelected(next);
  };

  const toggleOne = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelected(next);
  };

  const selectionStats = useMemo(() => {
    let docs = 0;
    let mb = 0;
    for (const c of collections) {
      if (selected.has(c.name)) {
        docs += c.doc_count || 0;
        mb += c.size_mb || 0;
      }
    }
    return { docs, mb, count: selected.size };
  }, [collections, selected]);

  const startPolling = (jobId, kind) => {
    if (pollRef.current) clearInterval(pollRef.current);
    setActiveJob({ id: jobId, kind, status: 'in_progress', progress: null });
    pollRef.current = setInterval(async () => {
      try {
        const r = await apiClient.get(`/backup/job/${jobId}`);
        const d = r.data || {};
        setActiveJob({
          id: jobId, kind,
          status: d.status,
          progress: d.progress,
          result: d,
        });
        if (d.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (kind === 'export') {
            toast.success(
              `Export complete — ${fmtNum(d.total_documents)} docs, ${fmtMB(d.size_mb)}.`,
            );
          } else {
            toast.success(`Import complete — ${fmtNum(d.total_inserted)} docs restored.`);
          }
          await loadAll();
          if (onChanged) onChanged();
          setTimeout(() => setActiveJob(null), 1500);
        } else if (d.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.error(`${kind === 'export' ? 'Export' : 'Import'} failed: ${d.error || 'unknown'}`);
          setTimeout(() => setActiveJob(null), 2000);
        }
      } catch (e) {
        console.error('poll error', e);
      }
    }, POLL_INTERVAL_MS);
  };

  // ---------------------------------------------------------------------
  // Export
  // ---------------------------------------------------------------------
  const handleExport = async () => {
    if (selected.size === 0) {
      toast.error('Pick at least one collection.');
      return;
    }
    try {
      const r = await apiClient.post('/backup/collections/export', {
        collections: Array.from(selected),
      });
      toast.info('Export started…');
      startPolling(r.data.job_id, 'export');
    } catch (e) {
      toast.error(errMsg(e, 'Export failed to start'));
    }
  };

  const handleDownloadExport = async (exportId) => {
    try {
      const r = await apiClient.get(`/backup/collections/download/${exportId}`);
      const a = document.createElement('a');
      a.href = r.data.download_url;
      a.download = `${exportId}.tar.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error('Failed to fetch download URL');
    }
  };

  const handleDeleteExport = async (exportId) => {
    if (!window.confirm(`Delete selective backup ${exportId}?`)) return;
    try {
      await apiClient.delete(`/backup/collections/${exportId}`);
      toast.success('Selective backup deleted');
      loadAll();
    } catch (e) {
      toast.error(errMsg(e, 'Delete failed'));
    }
  };

  // ---------------------------------------------------------------------
  // Import (upload → inspect → restore)
  // ---------------------------------------------------------------------
  const resetImport = () => {
    setImportFile(null);
    setImportUploadId(null);
    setImportInspect(null);
    setImportSelected(new Set());
    setImportStage('select');
    setUploadProgress(0);
    setImportConfirmText('');
  };

  const openImportDialog = () => {
    resetImport();
    setImportOpen(true);
  };

  const closeImportDialog = () => {
    setImportOpen(false);
    resetImport();
  };

  const handleUploadInspect = async () => {
    if (!importFile) {
      toast.error('Choose a .tar.gz selective archive first.');
      return;
    }
    if (!/\.(tar\.gz|tgz|tar)$/i.test(importFile.name)) {
      toast.error('Expected a .tar.gz or .tar selective archive.');
      return;
    }
    setImportStage('uploading');
    try {
      // Setting `Content-Type: undefined` lets the browser set the correct
      // `multipart/form-data; boundary=…` header — the apiClient default
      // `application/json` otherwise stops FastAPI parsing the multipart
      // body and produces three `Field required` validation errors.
      const fdCfg = { headers: { 'Content-Type': undefined } };

      const totalChunks = Math.ceil(importFile.size / CHUNK_SIZE);
      const initForm = new FormData();
      initForm.append('filename', importFile.name);
      initForm.append('total_size', importFile.size);
      initForm.append('total_chunks', totalChunks);
      const initRes = await apiClient.post('/backup/upload/init', initForm, fdCfg);
      const uploadId = initRes.data.upload_id;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, importFile.size);
        const chunkForm = new FormData();
        chunkForm.append('upload_id', uploadId);
        chunkForm.append('chunk_index', i);
        chunkForm.append('chunk', importFile.slice(start, end), `chunk_${i}`);
        await apiClient.post('/backup/upload/chunk', chunkForm, fdCfg);
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      const completeForm = new FormData();
      completeForm.append('upload_id', uploadId);
      await apiClient.post('/backup/upload/complete', completeForm, fdCfg);

      const inspectRes = await apiClient.get(`/backup/collections/inspect-upload/${uploadId}`);
      setImportUploadId(uploadId);
      setImportInspect(inspectRes.data);
      // Pre-select every collection in the archive by default.
      setImportSelected(new Set((inspectRes.data.collections || []).map((c) => c.name)));
      setImportStage('inspect');
    } catch (e) {
      console.error(e);
      toast.error(errMsg(e, 'Upload / inspect failed'));
      setImportStage('select');
    }
  };

  const handleConfirmImport = async () => {
    if (importSelected.size === 0) {
      toast.error('Pick at least one collection.');
      return;
    }
    if (importConfirmText.trim() !== 'REPLACE') {
      toast.error('Type REPLACE to confirm.');
      return;
    }
    try {
      const r = await apiClient.post('/backup/collections/import', {
        upload_id: importUploadId,
        collections: Array.from(importSelected),
        confirm: true,
      });
      toast.warning('Restore in progress — selected collections are being replaced.');
      closeImportDialog();
      startPolling(r.data.job_id, 'import');
    } catch (e) {
      toast.error(errMsg(e, 'Import failed to start'));
    }
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  return (
    <Card data-testid="selective-backup-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Boxes className="w-5 h-5" />
          Selective Collection Backup
        </CardTitle>
        <CardDescription>
          Export or restore specific collections without touching the rest of
          the database. Runs separately from the full-DB backup pipeline.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Active job status */}
        {activeJob && (
          <div
            className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
            data-testid="selective-active-job"
          >
            <Loader2 className="w-4 h-4 animate-spin text-amber-700" />
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-900">
                {activeJob.kind === 'export' ? 'Selective export' : 'Selective import'} —{' '}
                {activeJob.progress?.phase || activeJob.status}
                {typeof activeJob.progress?.percent === 'number'
                  ? ` (${activeJob.progress.percent}%)`
                  : ''}
              </div>
              {activeJob.progress?.message && (
                <div className="text-xs text-amber-800 mt-0.5">
                  {activeJob.progress.message}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              data-testid="selective-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search collections…"
              className="pl-9"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={loadAll}
            disabled={loadingList}
            data-testid="selective-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingList ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={toggleAllInView}
            disabled={loadingList || filtered.length === 0}
            data-testid="selective-select-all-btn"
          >
            {allSelectedInView ? 'Deselect All' : 'Select All'}
          </Button>
          <Button
            type="button"
            onClick={handleExport}
            disabled={disabled || activeJob !== null || selected.size === 0}
            className="bg-emerald-700 hover:bg-emerald-800"
            data-testid="selective-export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Selected ({selected.size})
          </Button>
          <Button
            type="button"
            onClick={openImportDialog}
            disabled={disabled || activeJob !== null}
            variant="outline"
            className="border-amber-600 text-amber-700 hover:bg-amber-50"
            data-testid="selective-import-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import (Replace)
          </Button>
        </div>

        {/* Selection summary */}
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-stone-600">
          <span>Total: <strong>{collections.length}</strong> collections, {fmtNum(totals.docs)} docs, {fmtMB(totals.mb)}</span>
          <span className="text-emerald-700">
            Selected: <strong>{selectionStats.count}</strong>, {fmtNum(selectionStats.docs)} docs, {fmtMB(selectionStats.mb)}
          </span>
        </div>

        {/* Collection table */}
        <div className="border rounded-lg overflow-hidden max-h-[420px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50 sticky top-0">
                <TableHead className="w-10"></TableHead>
                <TableHead>Collection</TableHead>
                <TableHead className="text-right">Docs</TableHead>
                <TableHead className="text-right">Data MB</TableHead>
                <TableHead className="text-right">Storage MB</TableHead>
                <TableHead className="text-right">Indexes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingList && filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-stone-500">
                    <RefreshCw className="w-6 h-6 mx-auto animate-spin text-stone-400" />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-stone-400">
                    No collections match.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => (
                  <TableRow
                    key={c.name}
                    className={selected.has(c.name) ? 'bg-emerald-50/40' : ''}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selected.has(c.name)}
                        onCheckedChange={() => toggleOne(c.name)}
                        data-testid={`coll-check-${c.name}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{c.name}</TableCell>
                    <TableCell className="text-right">{fmtNum(c.doc_count)}</TableCell>
                    <TableCell className="text-right">{(c.size_mb || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{(c.storage_mb || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{c.index_count || 0}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Previously-exported selective backups */}
        {exports.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-stone-700">
              <FileArchive className="w-4 h-4" />
              Previous Selective Backups
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead>Export ID</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exports.map((b) => (
                    <TableRow key={b.export_id}>
                      <TableCell className="font-mono text-xs">{b.export_id}</TableCell>
                      <TableCell className="text-xs text-stone-600">
                        {b.created_at ? new Date(b.created_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-right">{fmtMB(b.size_mb)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadExport(b.export_id)}
                            data-testid={`sel-download-${b.export_id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteExport(b.export_id)}
                            className="text-red-600"
                            data-testid={`sel-delete-${b.export_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => (o ? null : closeImportDialog())}>
        <DialogContent className="max-w-2xl" data-testid="selective-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-600" />
              Import Selective Backup (Replace Mode)
            </DialogTitle>
            <DialogDescription>
              Selected collections will be <strong>dropped and reinserted</strong> from the archive.
              Other collections are untouched.
            </DialogDescription>
          </DialogHeader>

          {importStage === 'select' && (
            <div className="space-y-3">
              <input
                type="file"
                accept=".tar.gz,.tgz,.tar"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="block w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-stone-100 file:text-stone-700"
                data-testid="selective-import-file-input"
              />
              {importFile && (
                <div className="text-xs text-stone-600">
                  {importFile.name} — {fmtMB(importFile.size / (1024 * 1024))}
                </div>
              )}
            </div>
          )}

          {importStage === 'uploading' && (
            <div className="py-6 space-y-3">
              <div className="flex items-center gap-2 text-sm text-stone-700">
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading & inspecting archive…
              </div>
              <div className="h-2 bg-stone-200 rounded">
                <div
                  className="h-full bg-amber-500 rounded transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <div className="text-xs text-stone-500">{uploadProgress}%</div>
            </div>
          )}

          {(importStage === 'inspect' || importStage === 'confirm') && importInspect && (
            <div className="space-y-3">
              <div className="text-xs text-stone-600">
                <div><strong>Archive:</strong> {importInspect.filename}</div>
                {importInspect.metadata?.exported_at && (
                  <div><strong>Exported:</strong> {new Date(importInspect.metadata.exported_at).toLocaleString()}</div>
                )}
                {importInspect.metadata?.exported_by && (
                  <div><strong>By:</strong> {importInspect.metadata.exported_by}</div>
                )}
              </div>
              <div className="border rounded max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50 sticky top-0">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Collection</TableHead>
                      <TableHead className="text-right">Docs</TableHead>
                      <TableHead className="text-right">Size</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(importInspect.collections || []).map((c) => (
                      <TableRow key={c.name}>
                        <TableCell>
                          <Checkbox
                            checked={importSelected.has(c.name)}
                            onCheckedChange={() => {
                              const next = new Set(importSelected);
                              if (next.has(c.name)) next.delete(c.name); else next.add(c.name);
                              setImportSelected(next);
                            }}
                            data-testid={`imp-check-${c.name}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{c.name}</TableCell>
                        <TableCell className="text-right">{fmtNum(c.document_count)}</TableCell>
                        <TableCell className="text-right">{fmtMB(c.size_mb)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 flex gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  These <strong>{importSelected.size}</strong> collection(s) will be{' '}
                  <strong>DROPPED</strong> and replaced with archive contents.
                  Existing data will be lost.
                </div>
              </div>
              <div>
                <label className="text-xs text-stone-600">
                  Type <strong>REPLACE</strong> to confirm:
                </label>
                <Input
                  value={importConfirmText}
                  onChange={(e) => setImportConfirmText(e.target.value)}
                  placeholder="REPLACE"
                  data-testid="selective-import-confirm-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {importStage === 'select' && (
              <>
                <Button variant="outline" onClick={closeImportDialog}>Cancel</Button>
                <Button
                  onClick={handleUploadInspect}
                  disabled={!importFile}
                  data-testid="selective-import-upload-btn"
                >
                  Upload & Inspect
                </Button>
              </>
            )}
            {importStage === 'inspect' && (
              <>
                <Button variant="outline" onClick={closeImportDialog}>Cancel</Button>
                <Button
                  onClick={handleConfirmImport}
                  disabled={importSelected.size === 0 || importConfirmText.trim() !== 'REPLACE'}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="selective-import-restore-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Restore {importSelected.size} Collection(s)
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
